import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  Bar, BarChart, Line, LineChart, Area, AreaChart, Pie, PieChart, Cell,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { useWidgetData } from '../../../api/hooks/useAnalytics';
import type { SeriesPoint, WidgetSpec } from '../../../types/analytics';

const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2'];
const MIN_RENDER_SIZE = 24;
const MAX_AXIS_TICK_LINES = 2;
const AVG_AXIS_CHAR_WIDTH = 6.5;

const fmt = (v: number, formatting: Record<string, unknown>) =>
  formatting?.unit === 'currency'
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(v)
    : new Intl.NumberFormat().format(v);

function useMeasuredChartFrame() {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;

    const update = (width: number, height: number) => {
      const next = {
        width: Math.max(0, Math.floor(width)),
        height: Math.max(0, Math.floor(height)),
      };
      setSize((prev) => (
        Math.abs(prev.width - next.width) > 1 || Math.abs(prev.height - next.height) > 1
          ? next
          : prev
      ));
    };

    const measure = () => {
      const rect = el.getBoundingClientRect();
      update(rect.width, rect.height);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(([entry]) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const rect = entry?.contentRect;
        if (rect) update(rect.width, rect.height);
      });
    });

    observer.observe(el);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      observer.disconnect();
    };
  }, []);

  return { frameRef, size };
}

export function WidgetRenderer({ title, spec }: { title: string; spec: WidgetSpec }) {
  const { data, isLoading, isError } = useWidgetData(spec);
  const { frameRef, size } = useMeasuredChartFrame();
  const canRenderChart = size.width >= MIN_RENDER_SIZE && size.height >= MIN_RENDER_SIZE;

  return (
    <div className="h-full w-full flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
      <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2 truncate">{title}</div>
      <div ref={frameRef} className="flex-1 min-h-0 relative overflow-hidden">
        {isLoading && <div className="text-xs text-gray-400">Loading…</div>}
        {isError && <div className="text-xs text-red-500">Failed to load</div>}
        {data && data.series.length === 0 && <div className="text-xs text-gray-400">No data</div>}
        {data && data.series.length > 0 && (
          spec.chart_type === 'kpi' ? (
            <div className="flex h-full items-center">
              <span className="text-3xl font-bold text-gray-900 dark:text-gray-50">
                {fmt(data.series.reduce((s, p) => s + p.value, 0), data.meta.formatting as Record<string, unknown>)}
              </span>
            </div>
          ) : (
            <div className="absolute inset-0 flex items-stretch justify-stretch">
              {canRenderChart ? renderChart(spec, data.series, size) : null}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function renderChart(spec: WidgetSpec, series: SeriesPoint[], size: { width: number; height: number }): ReactNode {
  const common = { data: series, width: size.width, height: size.height };
  const categorySlotWidth = series.length > 0 ? size.width / series.length : size.width;
  const showCategoryTicks = series.length <= 12 && categorySlotWidth >= 44;
  switch (spec.chart_type) {
    case 'line':
      return (<LineChart {...common}><XAxis dataKey="label" hide={series.length > 8} /><YAxis /><Tooltip />
        <Line type="monotone" dataKey="value" stroke={COLORS[0]} isAnimationActive={false} /></LineChart>);
    case 'area':
      return (<AreaChart {...common}><XAxis dataKey="label" hide={series.length > 8} /><YAxis /><Tooltip />
        <Area type="monotone" dataKey="value" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.2}
          isAnimationActive={false} /></AreaChart>);
    case 'pie':
      return (<PieChart {...common}><Tooltip /><Pie data={series} dataKey="value" nameKey="label" outerRadius="80%"
        isAnimationActive={false}>
        {series.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie></PieChart>);
    case 'bar':
    default:
      return (<BarChart {...common} margin={{ top: 5, right: 5, bottom: showCategoryTicks ? 18 : 5, left: 5 }}>
        <XAxis
          dataKey="label"
          hide={!showCategoryTicks}
          interval={0}
          height={showCategoryTicks ? 54 : 30}
          tick={<CategoryAxisTick slotWidth={categorySlotWidth} />} />
        <YAxis /><Tooltip />
        <Bar dataKey="value" fill={COLORS[0]} isAnimationActive={false} /></BarChart>);
  }
}

function CategoryAxisTick({ x = 0, y = 0, payload, slotWidth }: {
  x?: number;
  y?: number;
  payload?: { value?: unknown };
  slotWidth: number;
}) {
  const lines = wrapAxisLabel(String(payload?.value ?? ''), slotWidth);

  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#6b7280" fontSize={11}>
        {lines.map((line, index) => (
          <tspan key={`${line}-${index}`} x={0} dy={index === 0 ? 12 : 13}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
}

function wrapAxisLabel(label: string, slotWidth: number) {
  const maxChars = Math.max(4, Math.floor((slotWidth - 8) / AVG_AXIS_CHAR_WIDTH));
  const words = label.trim().split(/\s+/).filter(Boolean);
  const lines: string[] = [];

  while (words.length > 0 && lines.length < MAX_AXIS_TICK_LINES) {
    let line = '';
    while (words.length > 0) {
      const candidate = line ? `${line} ${words[0]}` : words[0];
      if (candidate.length > maxChars) break;
      line = candidate;
      words.shift();
    }

    if (!line && words.length > 0) {
      line = truncateAxisLabel(words.shift() ?? '', maxChars);
    }

    lines.push(line);
  }

  if (words.length > 0 && lines.length > 0) {
    lines[lines.length - 1] = appendAxisEllipsis(lines[lines.length - 1], maxChars);
  }

  return lines.length > 0 ? lines : [''];
}

function appendAxisEllipsis(label: string, maxChars: number) {
  if (maxChars <= 3) return label.slice(0, maxChars);
  return `${label.slice(0, maxChars - 3)}...`;
}

function truncateAxisLabel(label: string, maxChars: number) {
  if (label.length <= maxChars) return label;
  return appendAxisEllipsis(label, maxChars);
}
